import { useEffect, useRef, useState } from "react";
import type { ChangeEvent } from "react";
import { Link, useSearchParams } from "react-router";
import { Skeleton } from "@/components/skeleton";
import { cn } from "@/lib/utils";
import {
  useWorkflowList,
  useWorkflow,
  useWorkflowTemplates,
  createWorkflow,
  createWorkflowFromTemplate,
  exportWorkflowPackage,
  importWorkflowPackage,
  activateWorkflow,
  pauseWorkflow,
} from "@/hooks/use-workflows";
import { ApiError } from "@/lib/api-client";
import { EmptyState } from "@/components/ui/empty-state";
import { Search, Wand2, Zap } from "lucide-react";
import { WorkflowEditor } from "./editor";
import { WorkflowComplianceBadge, WorkflowCompliancePanel } from "./compliance-display";

type StatusFilter = "all" | "draft" | "active" | "paused";

/** Workflow automations page — list + editor view. */
export default function AutomationsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const importInputRef = useRef<HTMLInputElement | null>(null);

  const apiStatus = statusFilter === "all" ? undefined : statusFilter;
  const { workflows, isLoading, error, refetch } = useWorkflowList(apiStatus);
  const { workflow: editingWorkflow, version, isLoading: editorLoading } = useWorkflow(editingId);
  const { templates, isLoading: templatesLoading } = useWorkflowTemplates();
  const requestedWorkflowId = searchParams.get("workflowId");

  useEffect(() => {
    if (requestedWorkflowId && requestedWorkflowId !== editingId) {
      setEditingId(requestedWorkflowId);
    }
  }, [requestedWorkflowId, editingId]);

  const openWorkflow = (workflowId: string) => {
    setEditingId(workflowId);
    const next = new URLSearchParams(searchParams);
    next.set("workflowId", workflowId);
    setSearchParams(next, { replace: true });
  };

  const closeWorkflow = () => {
    setEditingId(null);
    const next = new URLSearchParams(searchParams);
    next.delete("workflowId");
    setSearchParams(next, { replace: true });
    refetch();
  };

  // If editing, show the editor full-screen
  if (editingId && editingWorkflow) {
    return (
      <WorkflowEditor
        workflowId={editingWorkflow.id}
        workflowName={editingWorkflow.name}
        initialNodes={version?.nodes ?? []}
        initialEdges={version?.edges ?? []}
        compliance={editingWorkflow.compliance}
        quietHours={editingWorkflow.quietHours}
        frequencyCap={editingWorkflow.frequencyCap}
        onBack={closeWorkflow}
      />
    );
  }

  if (editingId && editorLoading) {
    return <div className="flex h-64 items-center justify-center"><p className="text-muted-foreground">Loading workflow...</p></div>;
  }

  const handleCreate = async (name: string) => {
    setCreateError(null);
    try {
      const wf = await createWorkflow({ name, triggers: [{ type: "trigger_manual" }] });
      openWorkflow(wf.id);
      setShowCreate(false);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setCreateError("Workflow creation is not available yet. The backend service is being set up.");
      } else {
        setCreateError(err instanceof Error ? err.message : "Failed to create workflow");
      }
      setShowCreate(false);
    }
  };

  const handleCreateFromTemplate = async (templateId: string, name?: string) => {
    setCreateError(null);
    try {
      const result = await createWorkflowFromTemplate(templateId, name);
      openWorkflow(result.workflowId);
      setShowCreate(false);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : "Failed to create workflow from template");
      setShowCreate(false);
    }
  };

  const handleExportWorkflow = async (workflowId: string, workflowName: string) => {
    setCreateError(null);
    try {
      const workflowPackage = await exportWorkflowPackage(workflowId);
      const blob = new Blob([JSON.stringify(workflowPackage, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${workflowName.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "") || "workflow"}.workflow.json`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : "Failed to export workflow");
    }
  };

  const handleImportWorkflow = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    setCreateError(null);
    try {
      const parsed = JSON.parse(await file.text());
      const result = await importWorkflowPackage(parsed);
      openWorkflow(result.workflowId);
      refetch();
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : "Failed to import workflow package");
    }
  };

  const handleToggleStatus = async (id: string, currentStatus: string) => {
    try {
      if (currentStatus === "active") await pauseWorkflow(id);
      else await activateWorkflow(id);
      refetch();
    } catch { /* silently fail */ }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-foreground">Automations</h1>
        <div className="flex items-center gap-2">
          <input
            ref={importInputRef}
            type="file"
            accept="application/json,.json"
            className="hidden"
            onChange={handleImportWorkflow}
          />
          <button
            onClick={() => importInputRef.current?.click()}
            className="rounded-md border border-border px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-accent"
          >
            Import JSON
          </button>
          <button onClick={() => setShowCreate(true)}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90">
            + New Workflow
          </button>
        </div>
      </div>

      {/* Status filter */}
      <div className="flex gap-1 rounded-lg border border-border p-1">
        {(["all", "draft", "active", "paused"] as const).map((s) => (
          <button key={s} onClick={() => setStatusFilter(s)}
            className={cn("flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
              statusFilter === s ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent")}>
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>

      {createError && (
        <div className="rounded-md border border-destructive/20 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          {createError}
        </div>
      )}

      {error && (
        <div className="rounded-md border border-destructive/20 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          {error} <button onClick={refetch} className="underline">Retry</button>
        </div>
      )}

      {isLoading ? (
        <div className="space-y-2">{Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-16 rounded-md" />
        ))}</div>
      ) : workflows.length === 0 ? (
        <EmptyState icon={<Zap className="h-8 w-8" />} title="No workflows yet"
          description="Create your first automation to streamline your business processes."
          actionLabel="Create workflow" onAction={() => setShowCreate(true)} />
      ) : (
        <div className="divide-y divide-border rounded-lg border border-border">
          {workflows.map((wf) => (
            <div key={wf.id} className="stagger-item flex flex-col gap-3 px-4 py-3 md:flex-row md:items-center md:justify-between">
              <div className="space-y-2">
                <button onClick={() => openWorkflow(wf.id)} className="text-left">
                  <p className="text-sm font-medium text-foreground hover:text-primary">{wf.name}</p>
                </button>
                <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                  <StatusBadge status={wf.status} />
                  <WorkflowComplianceBadge compliance={wf.compliance} />
                  {wf.triggers.length > 0 && wf.triggers[0] && <span>Trigger: {wf.triggers[0].type.replace("trigger_", "")}</span>}
                </div>
                <WorkflowCompliancePanel compliance={wf.compliance} compact />
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => handleToggleStatus(wf.id, wf.status)}
                  className={cn("rounded-md px-3 py-1 text-xs", wf.status === "active"
                    ? "border border-yellow-300 text-[var(--color-warning)] hover:bg-[var(--color-warning)]/5"
                    : "border border-[var(--color-success)]/30 text-[var(--color-success)] hover:bg-[var(--color-success)]/5")}>
                  {wf.status === "active" ? "Pause" : "Activate"}
                </button>
                <button onClick={() => handleExportWorkflow(wf.id, wf.name)}
                  className="rounded-md border border-border px-3 py-1 text-xs text-muted-foreground hover:bg-accent">
                  Export
                </button>
                <button onClick={() => openWorkflow(wf.id)}
                  className="rounded-md border border-border px-3 py-1 text-xs text-muted-foreground hover:bg-accent">
                  Edit
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create modal */}
      {showCreate && (
        <CreateModal
          templates={templates}
          templatesLoading={templatesLoading}
          onClose={() => setShowCreate(false)}
          onCreateBlank={handleCreate}
          onCreateFromTemplate={handleCreateFromTemplate}
        />
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    draft: "bg-[var(--color-muted)] text-[var(--color-muted-foreground)]",
    active: "bg-[var(--color-success)]/10 text-[var(--color-success)]",
    paused: "bg-[var(--color-warning)]/10 text-[var(--color-warning)]",
  };
  return <span className={cn("rounded-full px-2 py-0.5 text-xs font-medium", colors[status] ?? "")}>{status}</span>;
}

function CreateModal({
  onClose,
  onCreateBlank,
  onCreateFromTemplate,
  templates,
  templatesLoading,
}: {
  onClose: () => void;
  onCreateBlank: (name: string) => void;
  onCreateFromTemplate: (templateId: string, name?: string) => void;
  templates: Array<{
    id: string;
    name: string;
    description: string;
    category: string;
    vertical: string;
    triggerType: string;
    stepCount: number;
  }>;
  templatesLoading: boolean;
}) {
  const [name, setName] = useState("");
  const [mode, setMode] = useState<"blank" | "template">("blank");
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState<string>("all");
  const [vertical, setVertical] = useState<string>("all");
  const filteredTemplates = templates.filter((template) => {
    const matchesCategory = category === "all" || template.category === category;
    const matchesVertical = vertical === "all" || template.vertical === vertical;
    const searchValue = search.trim().toLowerCase();
    const matchesSearch =
      searchValue.length === 0 ||
      template.name.toLowerCase().includes(searchValue) ||
      template.description.toLowerCase().includes(searchValue) ||
      template.vertical.toLowerCase().includes(searchValue);
    return matchesCategory && matchesVertical && matchesSearch;
  });
  const categories = Array.from(new Set(["all", ...templates.map((template) => template.category)]));
  const verticals = Array.from(new Set(["all", ...templates.map((template) => template.vertical)]));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-4xl rounded-lg bg-card p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">New Workflow</h2>
          <div className="flex rounded-md border border-border p-1 text-xs">
            <button
              onClick={() => setMode("blank")}
              className={cn("rounded px-3 py-1", mode === "blank" ? "bg-primary text-primary-foreground" : "text-muted-foreground")}
            >
              Blank
            </button>
            <button
              onClick={() => setMode("template")}
              className={cn("rounded px-3 py-1", mode === "template" ? "bg-primary text-primary-foreground" : "text-muted-foreground")}
            >
              Template Gallery
            </button>
          </div>
        </div>

        {mode === "blank" ? (
          <div className="space-y-4">
            <input type="text" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Workflow name" autoFocus
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm" />
            <p className="text-sm text-muted-foreground">
              Start with an empty canvas and define the trigger, conditions, and actions yourself.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-lg border border-border bg-muted/20 p-3 text-sm text-muted-foreground">
              Built-in workflow gallery templates install immediately. For cross-tenant paid/shared packages, use the canonical{" "}
              <Link to="/marketplace" className="font-medium text-primary underline-offset-4 hover:underline">
                Marketplace
              </Link>
              .
            </div>
            <div className="flex flex-col gap-3">
              <label className="relative flex-1">
                <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search templates"
                  className="w-full rounded-md border border-border bg-background py-2 pl-9 pr-3 text-sm"
                />
              </label>
              <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                <div className="space-y-2">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">Category</p>
                  <div className="flex flex-wrap gap-2">
                    {categories.map((item) => (
                      <button
                        key={item}
                        onClick={() => setCategory(item)}
                        data-testid={`workflow-template-category-${item}`}
                        className={cn(
                          "rounded-full border px-3 py-1 text-xs",
                          category === item ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground",
                        )}
                      >
                        {item}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">Vertical</p>
                  <div className="flex flex-wrap gap-2">
                    {verticals.map((item) => (
                      <button
                        key={item}
                        onClick={() => setVertical(item)}
                        data-testid={`workflow-template-vertical-${item}`}
                        className={cn(
                          "rounded-full border px-3 py-1 text-xs",
                          vertical === item ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground",
                        )}
                      >
                        {item}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="max-h-[420px] overflow-y-auto rounded-lg border border-border">
              {templatesLoading ? (
                <div className="space-y-2 p-3">{Array.from({ length: 4 }).map((_, index) => (
                  <Skeleton key={index} className="h-24 rounded-md" />
                ))}</div>
              ) : filteredTemplates.length === 0 ? (
                <div className="p-6 text-sm text-muted-foreground">No templates matched this filter.</div>
              ) : (
                <div className="divide-y divide-border">
                  {filteredTemplates.map((template) => (
                    <button
                      key={template.id}
                      onClick={() => onCreateFromTemplate(template.id, name.trim() || template.name)}
                      className="flex w-full items-start justify-between gap-4 px-4 py-4 text-left hover:bg-accent/40"
                    >
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-foreground">{template.name}</p>
                          <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">{template.category}</span>
                          <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">{template.vertical}</span>
                        </div>
                        <p className="text-sm text-muted-foreground">{template.description}</p>
                        <div className="flex gap-3 text-xs text-muted-foreground">
                          <span>Trigger: {template.triggerType.replace("trigger_", "")}</span>
                          <span>{template.stepCount} steps</span>
                        </div>
                      </div>
                      <span className="inline-flex items-center gap-1 rounded-md border border-primary/20 px-3 py-1 text-xs text-primary">
                        <Wand2 className="h-3.5 w-3.5" />
                        Use template
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-md border border-border px-4 py-2 text-sm">Cancel</button>
          {mode === "blank" ? (
            <button onClick={() => name.trim() && onCreateBlank(name.trim())}
              disabled={!name.trim()}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50">
              Create
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
